import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { Text } from "@jugl-web/ui-components/cross-platform/Text";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import "@cyntler/react-doc-viewer/dist/index.css";
import mime from "mime";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { Linkify } from "@jugl-web/utils/utils/Linkify";
import { Mentionify } from "@jugl-web/utils/utils/Mentionify";
import animationData from "../../assets/loading-animation.json";
import { FilePreviewConfig, FilePreviewType } from "../../types";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import { ReactComponent as UnknownFileIcon } from "./assets/unknown-file-icon.svg";
import "./index.css";
import { DocViewerHelper } from "./components/DocViewerHelper";

const UNKNOWN_FILE_EXTENSION = "bin";

const MULTI_ITEMS_SELECTOR_HEIGHT_PIXELS = 72;
const WEB_HEADER_PREVIEW_HEIGHT_PIXELS = 120;
const WEB_ADDITIONAL_GAP_PIXELS = 90;
const MOBILE_HEADER_PREVIEW_HEIGHT_PIXELS = 50;
const MOBILE_ADDITIONAL_GAP_PIXELS = 30;

interface MediaPanelProps {
  config: FilePreviewConfig;
  previewType: FilePreviewType;
  isLoading: boolean;
  hasError: boolean;
  isZoomed: boolean;
  inMultipleFilesMode?: boolean;
  onZoomChange: (isZoomed: boolean) => void;
  onLoad: () => void;
  onError: () => void;
  onClose: () => void;
  onDownload?: () => void;
}

export const MediaPanel = forwardRef<HTMLDivElement, MediaPanelProps>(
  (
    {
      config,
      previewType,
      isLoading,
      hasError,
      inMultipleFilesMode,
      isZoomed,
      onZoomChange,
      onLoad,
      onError,
      onDownload,
      onClose,
    },
    ref
  ) => {
    const { isMobile } = useAppVariant();
    const { t } = useTranslations();
    const { isWeb } = useAppVariant();
    const fileExtension = mime.getExtension(config.mimeType);
    const imgRef = useRef<HTMLImageElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [renderedSize, setRenderedSize] = useState<{
      width: number;
      height: number;
    } | null>(null);

    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el || !isZoomed) return undefined;

      const scrollSpeed = 10;
      const edgeSize = 125;

      let animationFrameId: number | null = null;
      let mouseX = 0;
      let mouseY = 0;
      let isInside = false;

      const scrollStep = () => {
        if (!el || !isInside) return;

        const rect = el.getBoundingClientRect();
        const x = mouseX - rect.left;
        const y = mouseY - rect.top;

        if (y < edgeSize) {
          el.scrollTop -= scrollSpeed;
        } else if (y > rect.height - edgeSize) {
          el.scrollTop += scrollSpeed;
        }

        if (x < edgeSize) {
          el.scrollLeft -= scrollSpeed;
        } else if (x > rect.width - edgeSize) {
          el.scrollLeft += scrollSpeed;
        }

        animationFrameId = requestAnimationFrame(scrollStep);
      };

      const handleMouseMove = (e: MouseEvent) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
        isInside = true;

        if (!animationFrameId) {
          animationFrameId = requestAnimationFrame(scrollStep);
        }
      };

      const handleMouseLeave = () => {
        isInside = false;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }
      };

      el.addEventListener("mousemove", handleMouseMove);
      el.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        el.removeEventListener("mousemove", handleMouseMove);
        el.removeEventListener("mouseleave", handleMouseLeave);
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }, [isZoomed]);

    const previewContentHeightClasses = useMemo(() => {
      let headerPreviewHeight = isMobile
        ? MOBILE_HEADER_PREVIEW_HEIGHT_PIXELS
        : WEB_HEADER_PREVIEW_HEIGHT_PIXELS;

      let additionalPreviewGap = isMobile
        ? MOBILE_ADDITIONAL_GAP_PIXELS
        : WEB_ADDITIONAL_GAP_PIXELS;

      if (config.isHeaderHidden) {
        headerPreviewHeight = 0;
        additionalPreviewGap = 0;
      }

      return inMultipleFilesMode
        ? `calc(100dvh - ${headerPreviewHeight}px - ${MULTI_ITEMS_SELECTOR_HEIGHT_PIXELS}px - ${additionalPreviewGap}px)`
        : `calc(100dvh - ${headerPreviewHeight}px - ${additionalPreviewGap}px)`;
    }, [isMobile, config.isHeaderHidden, inMultipleFilesMode]);

    const docPreviewFileTypes = [
      "odt",
      "doc",
      "docx",
      "ppt",
      "pptx",
      "xls",
      "xlsx",
      "csv",
      "txt",
      "pdf",
    ];

    if (fileExtension && docPreviewFileTypes.includes(fileExtension)) {
      return (
        <div ref={ref} className="flex justify-center">
          <DocViewerHelper
            url={config.url}
            fileType={fileExtension}
            previewContentHeightClasses={previewContentHeightClasses}
            isMobile={isMobile}
          />
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className="jugl__custom-scrollbar jugl__custom-scrollbar-horizontal relative m-auto flex flex-col items-center justify-center overflow-hidden"
        style={
          isZoomed
            ? {
                width: "100%",
                height: "100%",
              }
            : {
                width: isMobile ? "95%" : "80%",
                height: previewContentHeightClasses,
              }
        }
        onClick={onClose}
      >
        {!hasError && (
          <div
            className="relative flex max-h-full max-w-full overflow-auto rounded-lg"
            onClick={(e) => e.stopPropagation()}
            ref={scrollContainerRef}
          >
            {previewType === "image" && (
              <>
                {isLoading && (
                  <div className="absolute top-1/2 left-1/2 z-0 -translate-x-1/2 -translate-y-1/2 transition-opacity">
                    <Lottie
                      options={{ animationData }}
                      height={70}
                      width={70}
                    />
                  </div>
                )}
                <img
                  ref={imgRef}
                  src={config.url}
                  alt={config.name}
                  className={cx(
                    "relative z-10 h-full w-full cursor-zoom-in object-contain select-none",
                    isZoomed && "cursor-zoom-out"
                  )}
                  style={
                    isZoomed && renderedSize
                      ? {
                          width: `${renderedSize.width * 2}px`,
                          height: `${renderedSize.height * 2}px`,
                        }
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isWeb) {
                      onZoomChange(!isZoomed);
                      setTimeout(() => {
                        const container = scrollContainerRef.current;
                        const image = imgRef.current;
                        if (container && image) {
                          const scrollX =
                            image.offsetWidth / 2 - container.clientWidth / 2;
                          const scrollY =
                            image.offsetHeight / 2 - container.clientHeight / 2;

                          container.scrollTo({
                            left: scrollX,
                            top: scrollY,
                          });
                        }
                      }, 0);
                    }
                  }}
                  onContextMenu={
                    !onDownload ? (event) => event.preventDefault() : undefined
                  }
                  onLoad={() => {
                    if (imgRef.current) {
                      const { clientWidth, clientHeight } = imgRef.current;
                      setRenderedSize({
                        width: clientWidth,
                        height: clientHeight,
                      });

                      onLoad();
                    }
                  }}
                  onError={onError}
                />
              </>
            )}
            {previewType === "video" && (
              <video
                className="relative z-10 h-full w-full"
                autoPlay
                controls
                onContextMenu={
                  !onDownload ? (event) => event.preventDefault() : undefined
                }
                controlsList={!onDownload ? "nodownload" : undefined}
                playsInline
                onClick={(event) => event.stopPropagation()}
                onCanPlay={onLoad}
                onError={onError}
              >
                <source src={config.url} />
              </video>
            )}
            {previewType === "audio" && (
              <audio
                className="relative z-10"
                autoPlay
                controls
                onContextMenu={
                  !onDownload ? (event) => event.preventDefault() : undefined
                }
                controlsList={!onDownload ? "nodownload" : undefined}
                onClick={(event) => event.stopPropagation()}
                onCanPlay={onLoad}
                onError={onError}
              >
                <source src={config.url} type={config.mimeType} />
              </audio>
            )}
          </div>
        )}
        {(previewType === "other" || hasError) && (
          <div
            className="flex flex-col items-center p-12"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="relative">
              <FileIcon />
              <Text
                variant="body3"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform font-medium text-primary"
              >
                {fileExtension === UNKNOWN_FILE_EXTENSION ? (
                  <UnknownFileIcon />
                ) : (
                  fileExtension
                )}
              </Text>
            </span>
            <span
              className={cx(
                "mt-6 overflow-hidden text-center font-secondary font-bold text-ellipsis text-white",
                isMobile ? "text-lg leading-5" : "text-2xl leading-7"
              )}
            >
              {config.name}
            </span>
            <span
              className={cx(
                "mt-2 mb-10 leading-4 font-normal text-white",
                isMobile ? "text-sm" : "text-base"
              )}
            >
              {t({
                id: "file-preview-component.preview-not-available",
                defaultMessage: "Preview is not available",
              })}
            </span>
            {onDownload && (
              <Button
                color="primary"
                variant="contained"
                className={cx(isMobile ? "h-10 w-[250px]" : "h-12 w-[300px]")}
                onClick={onDownload}
              >
                {t({
                  id: "common.download-file",
                  defaultMessage: "Download file",
                })}
              </Button>
            )}
          </div>
        )}
        {config.caption && !isZoomed && (
          <div
            className={cx(
              "jugl__custom-scrollbar jugl__break-word mt-7 max-h-[30%] w-full max-w-[70%] shrink-0 overflow-y-auto p-3 text-center font-secondary text-sm leading-[160%] whitespace-pre-line text-grey-400",
              isMobile && "max-w-[85%]"
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <Linkify>
              <Mentionify>{config.caption}</Mentionify>
            </Linkify>
          </div>
        )}
      </div>
    );
  }
);
